// ===============================================================
// 📸 BeautyPage.js — LIVORA Full Screen Beauty Camera
// - phase 3:
//   1) faster rendering pipeline
//   2) low-res processing canvas
//   3) no heavy pixel loop
//   4) soft face-area beauty mask
// ===============================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Sparkles } from "lucide-react";
import "./BeautyPage.css";

const FEMALE_FILTERS = [
  { key: "original", label: "Original" },
  { key: "velvet", label: "Velvet" },
  { key: "glow", label: "Glow" },
  { key: "soft", label: "Soft" },
];

const MALE_FILTERS = [
  { key: "original", label: "Original" },
  { key: "clean", label: "Clean" },
  { key: "studio", label: "Studio" },
];

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

export default function BeautyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const processCanvasRef = useRef(document.createElement("canvas"));
  const renderFrameRef = useRef(null);
  const streamRef = useRef(null);

  const [gender] = useState("female");

  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [starting, setStarting] = useState(true);
  const [usingFrontCamera, setUsingFrontCamera] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState("velvet");
  const [flipping, setFlipping] = useState(false);
  const [intensity, setIntensity] = useState(56);

  const filters = useMemo(() => {
    return gender === "female" ? FEMALE_FILTERS : MALE_FILTERS;
  }, [gender]);

  const stopRenderLoop = useCallback(() => {
    if (renderFrameRef.current) {
      cancelAnimationFrame(renderFrameRef.current);
      renderFrameRef.current = null;
    }
  }, []);

  const stopCurrentStream = useCallback(() => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    } catch (e) {
      console.warn("stopCurrentStream warning:", e);
    }
  }, []);

  const resizeCanvases = useCallback(() => {
    const displayCanvas = canvasRef.current;
    const processCanvas = processCanvasRef.current;
    if (!displayCanvas || !processCanvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    displayCanvas.width = Math.floor(viewportWidth * dpr);
    displayCanvas.height = Math.floor(viewportHeight * dpr);
    displayCanvas.style.width = `${viewportWidth}px`;
    displayCanvas.style.height = `${viewportHeight}px`;

    // معالجة أخف بكثير من العرض
    const processScale = 0.45;
    processCanvas.width = Math.max(320, Math.floor(displayCanvas.width * processScale));
    processCanvas.height = Math.max(568, Math.floor(displayCanvas.height * processScale));
  }, []);

  const drawCoverImage = useCallback((ctx, source, targetWidth, targetHeight) => {
    const sw = source.videoWidth || source.width;
    const sh = source.videoHeight || source.height;

    if (!sw || !sh || !targetWidth || !targetHeight) return;

    const sourceRatio = sw / sh;
    const targetRatio = targetWidth / targetHeight;

    let drawWidth;
    let drawHeight;
    let dx = 0;
    let dy = 0;

    if (sourceRatio > targetRatio) {
      drawHeight = targetHeight;
      drawWidth = drawHeight * sourceRatio;
      dx = (targetWidth - drawWidth) / 2;
    } else {
      drawWidth = targetWidth;
      drawHeight = drawWidth / sourceRatio;
      dy = (targetHeight - drawHeight) / 2;
    }

    ctx.clearRect(0, 0, targetWidth, targetHeight);
    ctx.drawImage(source, dx, dy, drawWidth, drawHeight);
  }, []);

  const createFaceMask = useCallback((ctx, width, height) => {
    const cx = width * 0.5;
    const cy = height * 0.43;
    const rx = width * 0.23;
    const ry = height * 0.22;

    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  }, []);

  const applyVelvetFilterFast = useCallback((ctx, canvas, level) => {
    const { width, height } = canvas;
    if (!width || !height) return;

    const power = clamp(level / 100, 0, 1);

    const globalBlur = 1.5 + power * 1.8;
    const faceBlur = 4 + power * 4.5;
    const globalAlpha = 0.05 + power * 0.05;
    const faceAlpha = 0.10 + power * 0.16;
    const warmAlpha = 0.05 + power * 0.08;
    const liftAlpha = 0.10 + power * 0.10;

    // طبقة تحسين عامة خفيفة
    ctx.save();
    ctx.globalAlpha = globalAlpha;
    ctx.filter = `blur(${globalBlur}px) brightness(${1.01 + power * 0.02}) saturate(${1.02 + power * 0.03})`;
    ctx.drawImage(canvas, 0, 0, width, height);
    ctx.restore();

    // طبقة أقوى في منطقة الوجه فقط
    ctx.save();
    createFaceMask(ctx, width, height);
    ctx.clip();
    ctx.globalAlpha = faceAlpha;
    ctx.filter = `blur(${faceBlur}px) brightness(${1.02 + power * 0.03}) saturate(${1.03 + power * 0.05})`;
    ctx.drawImage(canvas, 0, 0, width, height);
    ctx.restore();

    // دفء لوني خفيف على الوجه
    ctx.save();
    createFaceMask(ctx, width, height);
    ctx.clip();
    ctx.globalCompositeOperation = "soft-light";
    ctx.globalAlpha = warmAlpha;
    ctx.fillStyle = "rgba(255, 198, 216, 0.9)";
    ctx.fillRect(0, 0, width, height);
    ctx.restore();

    // رفع ناعم في مركز الوجه
    const gradient = ctx.createRadialGradient(
      width * 0.5,
      height * 0.42,
      width * 0.04,
      width * 0.5,
      height * 0.42,
      width * 0.34
    );
    gradient.addColorStop(0, `rgba(255,255,255,${0.18 + power * 0.08})`);
    gradient.addColorStop(0.36, `rgba(255,244,248,${0.08 + power * 0.05})`);
    gradient.addColorStop(1, "rgba(255,255,255,0)");

    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.globalAlpha = liftAlpha;
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();

    // لمعة خفيفة جدًا
    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.globalAlpha = 0.025 + power * 0.03;
    ctx.fillStyle = "rgba(255,255,255,1)";
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }, [createFaceMask]);

  const applyGlowFilterFast = useCallback((ctx, canvas, level) => {
    const { width, height } = canvas;
    if (!width || !height) return;

    const power = clamp(level / 100, 0, 1);

    ctx.save();
    ctx.globalAlpha = 0.12 + power * 0.14;
    ctx.filter = `blur(${4 + power * 5}px) brightness(${1.04 + power * 0.04}) saturate(${1.05 + power * 0.06})`;
    ctx.drawImage(canvas, 0, 0, width, height);
    ctx.restore();

    const gradient = ctx.createRadialGradient(
      width * 0.5,
      height * 0.4,
      width * 0.05,
      width * 0.5,
      height * 0.4,
      width * 0.7
    );
    gradient.addColorStop(0, `rgba(255,255,255,${0.15 + power * 0.08})`);
    gradient.addColorStop(0.32, `rgba(255,235,245,${0.07 + power * 0.05})`);
    gradient.addColorStop(1, "rgba(255,255,255,0)");

    ctx.save();
    ctx.globalCompositeOperation = "screen";
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }, []);

  const applySoftFilterFast = useCallback((ctx, canvas, level) => {
    const { width, height } = canvas;
    if (!width || !height) return;

    const power = clamp(level / 100, 0, 1);

    ctx.save();
    ctx.globalAlpha = 0.08 + power * 0.08;
    ctx.filter = `blur(${2 + power * 3}px) brightness(${1.01 + power * 0.02})`;
    ctx.drawImage(canvas, 0, 0, width, height);
    ctx.restore();
  }, []);

  const applySelectedFilter = useCallback(
    (ctx, canvas, filterKey, level) => {
      if (filterKey === "original") return;
      if (filterKey === "velvet") return applyVelvetFilterFast(ctx, canvas, level);
      if (filterKey === "glow") return applyGlowFilterFast(ctx, canvas, level);
      if (filterKey === "soft") return applySoftFilterFast(ctx, canvas, level);
      if (filterKey === "clean") return applySoftFilterFast(ctx, canvas, level);
      if (filterKey === "studio") return applyGlowFilterFast(ctx, canvas, level);
    },
    [applyGlowFilterFast, applySoftFilterFast, applyVelvetFilterFast]
  );

  const renderLoop = useCallback(() => {
    const video = videoRef.current;
    const displayCanvas = canvasRef.current;
    const processCanvas = processCanvasRef.current;

    if (!video || !displayCanvas || !processCanvas) {
      renderFrameRef.current = requestAnimationFrame(renderLoop);
      return;
    }

    const pctx = processCanvas.getContext("2d");
    const dctx = displayCanvas.getContext("2d");

    if (!pctx || !dctx) {
      renderFrameRef.current = requestAnimationFrame(renderLoop);
      return;
    }

    if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
      // 1) رسم الفيديو على canvas داخلي صغير
      drawCoverImage(pctx, video, processCanvas.width, processCanvas.height);

      // 2) تطبيق الفلتر السريع على الداخلي
      applySelectedFilter(pctx, processCanvas, selectedFilter, intensity);

      // 3) عرض النتيجة على canvas الشاشة
      dctx.clearRect(0, 0, displayCanvas.width, displayCanvas.height);
      dctx.imageSmoothingEnabled = true;
      dctx.imageSmoothingQuality = "high";
      dctx.drawImage(processCanvas, 0, 0, displayCanvas.width, displayCanvas.height);
    }

    renderFrameRef.current = requestAnimationFrame(renderLoop);
  }, [applySelectedFilter, drawCoverImage, intensity, selectedFilter]);

  const startCamera = useCallback(
    async (front = true) => {
      try {
        setStarting(true);
        setCameraError(t("beauty.camera_open_error", { defaultValue: "Could not open the camera on this device or browser." }));
        setCameraReady(false);

        stopRenderLoop();
        stopCurrentStream();

        const constraints = {
          audio: false,
          video: {
            facingMode: front ? "user" : "environment",
            frameRate: { ideal: 24, max: 24 },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
          } catch (playErr) {
            console.warn("video play warning:", playErr);
          }
        }

        resizeCanvases();
        renderFrameRef.current = requestAnimationFrame(renderLoop);

        setCameraReady(true);
      } catch (e) {
        console.error("BeautyPage camera error:", e);
        setCameraError(t("beauty.camera_open_error", { defaultValue: "Could not open the camera on this device or browser." }));
      } finally {
        setStarting(false);
      }
    },
    [renderLoop, resizeCanvases, stopCurrentStream, stopRenderLoop, t]
  );

  const handleFlipCamera = useCallback(async () => {
    if (flipping) return;

    try {
      setFlipping(true);
      const nextFront = !usingFrontCamera;
      setUsingFrontCamera(nextFront);
      await startCamera(nextFront);
    } catch (e) {
      console.error("handleFlipCamera error:", e);
    } finally {
      setFlipping(false);
    }
  }, [flipping, startCamera, usingFrontCamera]);

  useEffect(() => {
    resizeCanvases();
    startCamera(true);

    const onResize = () => resizeCanvases();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      stopRenderLoop();
      stopCurrentStream();
    };
  }, [resizeCanvases, startCamera, stopCurrentStream, stopRenderLoop]);

  return (
    <div className="beauty-camera-page">
      <div className="beauty-camera-stage">
        <video
          ref={videoRef}
          className={`beauty-camera-video ${usingFrontCamera ? "mirror" : ""}`}
          autoPlay
          muted
          playsInline
        />

        <canvas
          ref={canvasRef}
          className={`beauty-camera-canvas ${usingFrontCamera ? "mirror" : ""}`}
        />

        {!cameraReady && (
          <div className="beauty-camera-overlay">
            {starting
              ? t("beauty.starting_camera", { defaultValue: "Starting camera..." })
              : cameraError || t("beauty.camera_unavailable", { defaultValue: "Camera unavailable" })}
          </div>
        )}

        <div className="beauty-camera-topbar">
          <button
            type="button"
            className="beauty-camera-icon-btn"
            onClick={() => navigate(-1)}
            aria-label={t("common.back", { defaultValue: "Back" })}
            title={t("common.back", { defaultValue: "Back" })}
          >
            <ArrowLeft size={20} />
          </button>

          <div className="beauty-camera-title-pill">
            <Sparkles size={14} />
            <span>{t("beauty.title", { defaultValue: "Beauty" })}</span>
          </div>

          <button
            type="button"
            className={`beauty-camera-icon-btn ${flipping ? "busy" : ""}`}
            onClick={handleFlipCamera}
            aria-label={t("beauty.flip_camera", { defaultValue: "Flip Camera" })}
            title={t("beauty.flip_camera", { defaultValue: "Flip Camera" })}
          >
            <RefreshCw size={20} />
          </button>
        </div>

        <div className="beauty-camera-bottom">
          <div className="beauty-camera-intensity-wrap">
            <div className="beauty-camera-intensity-head">
              <span>{t("beauty.velvet_power", { defaultValue: "Velvet Power" })}</span>
              <strong>{intensity}</strong>
            </div>

            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={intensity}
              onChange={(e) => setIntensity(Number(e.target.value))}
              className="beauty-camera-intensity-slider"
            />
          </div>

          <div className="beauty-camera-filter-rail">
            {filters.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`beauty-filter-chip ${
                  selectedFilter === item.key ? "active" : ""
                }`}
                onClick={() => setSelectedFilter(item.key)}
              >
                <span className="beauty-filter-thumb" />
                <span className="beauty-filter-label">{t(`beauty.filters.${item.key}`, { defaultValue: item.label })}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
